import { client } from "./client";
import { signal } from "./signal";

/**
 * گزارش و بلاکِ بازیکن — همان قراردادِ `Backend/cmd/bazivand/safety_api.go` (docs/report-block.md).
 *
 * سه کار، سه شدت:
 *   - «بی‌صدا برای من» (game/chat.ts): فقط همین مرورگر، فقط چت، سرور خبر ندارد.
 *   - بلاک: روی حساب. پیام و صدای او را نه می‌بینی نه می‌شنوی، روی هر دستگاهی که با
 *     همین حساب باشی، و در اتاقی که تو میزبانی نمی‌نشیند (این آخری را سرور جلو می‌گیرد).
 *   - گزارش: برای ادمین، با چند پیامِ آخرِ چتِ همان بازیکن که سرور خودش کنارش می‌گذارد.
 *
 * پنهان‌کردنِ چت و صدا کارِ همین کلاینت است و با [[blocked]]: پیام به همه‌ی میز می‌رسد و
 * فقط چشمِ تو نمی‌بیندش — درست همان چیزی که بلاک‌کننده می‌خواهد.
 */

export type BlockRow = { id: string; name: string; avatar: string; at_ms: number };

/** شناسه‌ی بلاک‌شده‌ها — چت و صدا با همین فیلتر می‌شوند. */
export const blocked = signal<ReadonlySet<string>>(new Set());
/** فهرستِ کامل با نام، برای پروفایل. null = هنوز نپرسیده‌ایم. */
export const blockList = signal<readonly BlockRow[] | null>(null);

/** فهرستِ این بازیکن را گرفته‌ایم؟ (با عوض‌شدنِ حساب دوباره پرسیده می‌شود.) */
let loadedFor = "";

function adopt(rows: readonly BlockRow[] | undefined): void {
  const list = rows ?? [];
  blockList.value = list;
  blocked.value = new Set(list.map((r) => r.id));
}

export function isBlocked(playerId: string): boolean {
  return !!playerId && blocked.value.has(playerId);
}

/** فهرست از سرور. خطا = false و همان قبلی می‌مانَد. */
export async function loadBlocks(): Promise<boolean> {
  const who = client.playerId.value;
  try {
    const r = await client.rest<{ blocks: BlockRow[] }>("GET", "/v1/blocks");
    // جوابی که با حسابِ قبلی گرفته شد، مالِ این حساب نیست.
    if (client.playerId.value !== who) return false;
    adopt(r?.blocks);
    loadedFor = who;
    return true;
  } catch {
    return false;
  }
}

function codeOf(e: unknown): string {
  return e instanceof Error ? e.message : "network";
}

export async function blockPlayer(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await client.rest<{ blocks: BlockRow[] }>("POST", "/v1/blocks", { target: id });
    adopt(r?.blocks);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: codeOf(e) };
  }
}

export async function unblockPlayer(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await client.rest<{ blocks: BlockRow[] }>("DELETE", `/v1/blocks/${encodeURIComponent(id)}`);
    adopt(r?.blocks);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: codeOf(e) };
  }
}

export type ReportReason = "abuse" | "voice" | "profile" | "cheat" | "spam" | "other" | "reveal";

/** دلیل‌ها به ترتیبِ سرور (ws.ReportReasons). */
export const REPORT_REASONS: readonly { key: ReportReason; fa: string; hint: string; mafia?: boolean }[] = [
  { key: "abuse", fa: "فحش، توهین یا مزاحمت", hint: "در چت یا با صدا" },
  { key: "voice", fa: "صدای آزاردهنده", hint: "داد، صدای بلند، حرف زشت" },
  { key: "profile", fa: "نام یا عکس نامناسب", hint: "روی صندلی یا پروفایل" },
  { key: "cheat", fa: "تقلب یا تبانی", hint: "مثلاً با یک هم‌دست بازی می‌کند" },
  { key: "spam", fa: "تبلیغ یا پیام تکراری", hint: "" },
  { key: "other", fa: "چیز دیگر", hint: "در توضیح بنویس" },
  // پرتکرارترین شکایتِ بازیکن‌های مافیای رقیب؛ فقط سرِ میزِ مافیا نشان داده می‌شود
  { key: "reveal", fa: "لو دادن نقش (اطلاق)", hint: "نقش خودش یا دیگری را گفت", mafia: true },
];

export const NOTE_MAX = 300;

export type ReportInput = {
  target: string;
  reason: ReportReason;
  note?: string;
  game?: string;
  context?: "chat" | "seat" | "room" | "watch";
  block?: boolean;
};
export type ReportResult = { ok: true; duplicate: boolean; blocked: boolean } | { ok: false; error: string };

export async function reportPlayer(r: ReportInput): Promise<ReportResult> {
  try {
    const out = await client.rest<{ id: number; duplicate: boolean; blocked: boolean }>("POST", "/v1/reports", r);
    // بلاکِ کنارِ گزارش: فهرستِ تازه، تا چتِ او همین حالا پنهان شود.
    if (out?.blocked) void loadBlocks();
    return { ok: true, duplicate: !!out?.duplicate, blocked: !!out?.blocked };
  } catch (e) {
    return { ok: false, error: codeOf(e) };
  }
}

/** کدِ خطای سرور → جمله: چه شد و چه باید کرد. */
export function safetyError(code: string): string {
  switch (code) {
    case "bad_target": return "خودت را نمی‌شود.";
    case "not_found": return "این بازیکن دیگر نیست.";
    case "block_limit": return "فهرست بلاکت پر است؛ چند نفر را از پروفایل بردار.";
    case "too_many": return "زیاد امتحان کردی؛ کمی بعد دوباره بزن.";
    case "bad_reason": return "یک دلیل انتخاب کن.";
    case "unauthorized": return "اول وارد شو.";
    default: return "به سرور نرسید؛ دوباره بزن.";
  }
}

/** کلیدِ بازیِ میزی که الان رویش هستیم — برای گزارش. */
export function currentGame(): string {
  return client.table.value?.game || client.board.value?.game || (client.mafia.value ? "mafia" : "") ||
    (client.cbTable.value ? "chahar" : "") || (client.shTable.value ? "shelam" : "") ||
    client.room.value?.game || "";
}

// فهرست از همان لحظه‌ی وصل‌شدن لازم است — چتِ میز و صدا با آن فیلتر می‌شوند — و با
// عوض‌شدنِ حساب، فهرستِ حسابِ قبلی یک لحظه هم نباید بماند.
function sync(): void {
  const who = client.playerId.value;
  if (who !== loadedFor && loadedFor !== "") {
    loadedFor = "";
    adopt(undefined);
    blockList.value = null;
  }
  if (client.state.value === "online" && who && who !== loadedFor) void loadBlocks();
}
client.state.subscribe(sync);
client.playerId.subscribe(sync);
sync();
